// ============================================
// UI RENDERING - ゲームプレイ中のUI描画
// ============================================
// このファイルはメインUI描画の調整を担当します。
// 各UIコンポーネントは専用モジュールに分割されています:
// - score-display.ts: スコア・ウェーブ・コンボ・敵カウンター表示
// - ability-display.ts: アビリティストック・ボタン表示
// - progress-display.ts: パワー・シールド・進捗バー
// - boss-display.ts: ボス警告・HPバー
// - panel-effects.ts: パネル演出（グリッド・ストリーム・区切り・ドット）
// - game-area-hud.ts: ゲームエリア内HUD（画面レティクル）
// - lives-display.ts: 残機表示

import { getP5 } from '../../core/p5-instance';
import { drawScoreDisplay, drawWaveDisplay, drawComboDisplay, drawEnemyCounter } from './score-display.js';
import { drawAbilityButton } from './ability-display.js';
import { drawPowerLevelBar, drawShieldIndicator, drawWaveProgressBar } from './progress-display.js';
import { drawBossWarning, drawBossHPBar } from './boss-display.js';
import { drawPanelGrid, drawDataStream, drawHudDivider, drawPulseDot } from './panel-effects.js';
import { drawGameAreaHUD } from './game-area-hud.js';
import { drawLivesDisplay } from './lives-display.js';
import { CONFIG } from '../../config/game-config.js';
import { getRightPanelX, getLeftPanelCenterX, easeOutCubic } from '../../utils/helpers.js';
import { getPanelSlideProgress } from '../../store/index.js';
import { HUD_COLORS } from '../../config/hud-colors.js';

// パルスドット Y座標（ラベル横に配置）
const SCORE_PULSE_Y = 24;
const STAGE_PULSE_Y = 119;

/**
 * drawPanelBackgrounds() - 左右パネルの背景を描画
 * ゲーム要素（ミサイル、小惑星など）がパネル領域に侵入しないよう覆う
 */
function drawPanelBackgrounds(): void {
    const p = getP5();
    const panelWidth = CONFIG.layout.panelWidth;  // 120
    const rightPanelX = getRightPanelX();
    const leftCenterX = getLeftPanelCenterX();

    // パネルスライドオフセット
    const slideProgress = easeOutCubic(getPanelSlideProgress());
    const leftOffset = -panelWidth * (1 - slideProgress);

    p.noStroke();
    p.fill(HUD_COLORS.panelBg[0], HUD_COLORS.panelBg[1], HUD_COLORS.panelBg[2], HUD_COLORS.panelBgAlpha);

    // 左パネル背景（左からスライドイン）
    p.rect(leftOffset, 0, panelWidth, p.height);

    // 右パネル背景（右からスライドイン、getRightPanelXにオフセット適用済み）
    p.rect(rightPanelX, 0, panelWidth, p.height);

    // パネル演出（スライド完了後のみ描画）
    if (slideProgress > 0.8) {
        // グリッドパターン
        drawPanelGrid(p, leftOffset, 0, panelWidth, p.height);
        drawPanelGrid(p, rightPanelX, 0, panelWidth, p.height);

        // データストリーム（パネル端の流れる光）
        drawDataStream(p, leftOffset, 0, p.height);
        drawDataStream(p, rightPanelX + panelWidth - 2, 0, p.height);

        // セクション区切り線（左パネル）
        const dividerWidth = panelWidth - 20;
        drawHudDivider(p, leftOffset + 10, 107, dividerWidth);   // スコア ─ ステージ
        drawHudDivider(p, leftOffset + 10, 245, dividerWidth);   // ウェーブ進捗バー下
        drawHudDivider(p, leftOffset + 10, 460, dividerWidth);   // パワー上
        drawHudDivider(p, leftOffset + 10, 572, dividerWidth);   // シールド上

        // セクション区切り線（右パネル）
        drawHudDivider(p, rightPanelX + 10, 270, dividerWidth);  // コンボ上
        drawHudDivider(p, rightPanelX + 10, 345, dividerWidth);  // コンボ ─ ボス
        drawHudDivider(p, rightPanelX + 10, 480, dividerWidth);  // LIVES上

        // パルスドット（ラベル横）
        drawPulseDot(p, leftCenterX - 30, SCORE_PULSE_Y);
        drawPulseDot(p, leftCenterX - 25, STAGE_PULSE_Y);
    }
}

/**
 * drawUI() - ゲームプレイ中のUI描画
 */
export function drawUI(): void {
    const p = getP5();
    p.textFont('Orbitron');

    // パネル背景を最初に描画（ゲーム要素を覆う）
    drawPanelBackgrounds();

    // ボス警告表示
    drawBossWarning();

    // ゲームエリア内HUD（コーナーフレーム、十字線、レティクル等）
    drawGameAreaHUD();

    // スコア・ウェーブ・コンボ・敵カウンター
    drawScoreDisplay();
    drawWaveDisplay();
    drawEnemyCounter();
    drawComboDisplay();

    // パワーレベル・シールドインジケータ
    drawPowerLevelBar();
    drawShieldIndicator();

    // アビリティボタン
    drawAbilityButton();

    // ウェーブ進行状況バー
    drawWaveProgressBar();

    // ボスHPバー（右パネル）
    drawBossHPBar();

    // 残機表示（右パネル下部）
    drawLivesDisplay();
}
